import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Ticket, ArrowLeft, CheckCircle, AlertCircle } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Elements } from "@stripe/react-stripe-js";
import { stripePromise } from "@/lib/stripe";
import StripePaymentForm from "@/components/StripePaymentForm";
import Spinner from "@/components/Spinner";

// Ticket tiers: { price in £, entries }
const TICKET_TIERS = [
  { price: 2, entries: 2, label: "£2", description: "2 entries" },
  { price: 5, entries: 10, label: "£5", description: "10 entries" },
  { price: 10, entries: 25, label: "£10", description: "25 entries" },
  { price: 20, entries: 70, label: "£20", description: "70 entries" },
];

const PrizeDraw = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const userId = searchParams.get("user");

  const [selectedTier, setSelectedTier] = useState<typeof TICKET_TIERS[0] | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [ambassador, setAmbassador] = useState<{
    id: string;
    name: string;
    avatar_url?: string;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [paymentStep, setPaymentStep] = useState<'selection' | 'payment'>('selection');
  const [clientSecret, setClientSecret] = useState<string>('');
  const [awaitingDestination, setAwaitingDestination] = useState(false);

  useEffect(() => {
    const fetchAmbassador = async () => {
      if (!userId) {
        toast({
          title: "Error",
          description: "No ambassador selected",
          variant: "destructive"
        });
        navigate("/map");
        return;
      }

      try {
        const { data, error } = await supabase
          .from('safe_users')
          .select('id, name, avatar_url')
          .eq('id', userId)
          .single();

        if (error || !data) {
          console.error('Error fetching ambassador:', error);
          toast({
            title: "Error",
            description: "Could not load ambassador information",
            variant: "destructive"
          });
          navigate("/map");
          return;
        }

        // Verify name starts with "Ambassador"
        if (!data.name?.toLowerCase().startsWith('ambassador')) {
          toast({
            title: "Not an Ambassador",
            description: "Prize draw is only available for ambassadors",
            variant: "destructive"
          });
          navigate("/map");
          return;
        }

        setAmbassador(data);
      } catch (e) {
        console.error('Failed to fetch ambassador:', e);
        navigate("/map");
      } finally {
        setLoading(false);
      }
    };

    fetchAmbassador();
  }, [userId, toast, navigate]);

  const handlePurchase = async () => {
    if (!selectedTier || !ambassador) return;

    // Check authentication
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      toast({
        title: "Error",
        description: "You must be logged in to purchase tickets",
        variant: "destructive"
      });
      return;
    }

    setIsProcessing(true);

    try {
      const { data, error } = await supabase.functions.invoke('create-prize-draw-entry', {
        body: {
          ambassador_id: ambassador.id,
          amount: selectedTier.price,
        }
      });

      if (error) {
        console.error('Error creating prize draw entry:', error);
        throw new Error((error as any)?.message || 'Failed to create prize draw entry');
      }

      if (data?.error) {
        throw new Error(data.error);
      }

      if (data?.clientSecret) {
        setClientSecret(data.clientSecret);
        setAwaitingDestination(!!data.awaitingDestination);
        setPaymentStep('payment');
      } else {
        throw new Error('No payment information returned');
      }
    } catch (error: any) {
      console.error('Failed to purchase tickets:', error);
      toast({
        title: "Failed to purchase tickets",
        description: error.message || "An unexpected error occurred. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spinner size={32} />
      </div>
    );
  }

  // Payment step
  if (paymentStep === 'payment' && clientSecret && selectedTier) {
    return (
      <div className="min-h-screen bg-gradient-warm pb-safe">
        <div className="container mx-auto px-4 py-6 pb-8 max-w-md md:max-w-2xl lg:max-w-4xl">
          <div className="mb-6">
            <Button
              variant="ghost"
              onClick={() => {
                setPaymentStep('selection');
                setClientSecret('');
              }}
              className="mb-4"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <h1 className="text-2xl font-bold text-center bg-gradient-sunset bg-clip-text text-transparent">
              Complete Payment
            </h1>
          </div>

          <Card className="mb-4 shadow-warm border-border/50">
            <CardHeader>
              <div className="flex items-center gap-4">
                <Avatar className="w-12 h-12 border-2 border-white shadow-soft">
                  <AvatarImage src={ambassador?.avatar_url || undefined} />
                  <AvatarFallback>
                    {ambassador?.name ? ambassador.name[0].toUpperCase() : '?'}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <CardTitle className="text-lg">{selectedTier.label} - {selectedTier.entries} entries</CardTitle>
                  <CardDescription>Prize draw with {ambassador?.name}</CardDescription>
                </div>
              </div>
            </CardHeader>
          </Card>

          {awaitingDestination && (
            <Card className="mb-4 border-amber-300/60 bg-amber-50 text-amber-900">
              <CardContent className="pt-6 text-sm">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-4 h-4 mt-0.5" />
                  <div>
                    This ambassador hasn't finished setting up payouts yet. We'll hold your payment securely and transfer their share once they complete setup.
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          <Card className="shadow-soft border-border/50">
            <CardContent className="pt-6">
              <Elements 
                stripe={stripePromise} 
                options={{ 
                  clientSecret,
                  appearance: {
                    theme: 'stripe',
                    variables: {
                      colorPrimary: '#F97316',
                    },
                  },
                }}
              >
                <StripePaymentForm
                  amount={selectedTier.price}
                  returnUrl={`${window.location.origin}/prize-draw-confirmation?entries=${selectedTier.entries}`}
                  onSuccess={() => navigate(`/prize-draw-confirmation?entries=${selectedTier.entries}`)}
                  onCancel={() => {
                    setPaymentStep('selection');
                    setClientSecret('');
                  }}
                />
              </Elements>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-warm pb-safe">
      <div className="container mx-auto px-4 py-6 pb-8 max-w-md md:max-w-2xl lg:max-w-4xl">
        {/* Header */}
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <h1 className="text-2xl font-bold text-center bg-gradient-sunset bg-clip-text text-transparent">
            🎟️ Prize Draw Entry
          </h1>
        </div>

        {/* Ambassador Card */}
        {ambassador && (
          <Card className="mb-6 shadow-warm border-border/50">
            <CardHeader>
              <div className="flex items-center gap-4">
                <div className="relative">
                  <div className="absolute inset-0 bg-primary/20 blur-lg rounded-full" />
                  <Avatar className="relative w-16 h-16 border-2 border-white shadow-soft">
                    <AvatarImage src={ambassador.avatar_url || undefined} />
                    <AvatarFallback>
                      {ambassador.name ? ambassador.name[0].toUpperCase() : '?'}
                    </AvatarFallback>
                  </Avatar>
                </div>
                <div>
                  <CardTitle>{ambassador.name}</CardTitle>
                  <CardDescription className="flex items-center gap-1">
                    <Ticket className="w-3 h-3 text-primary" />
                    Enter for a chance to win!
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
          </Card>
        )}

        {/* Ticket Selection */}
        <Card className="mb-6 shadow-soft border-border/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Ticket className="w-5 h-5 text-primary" />
              Select Your Tickets
            </CardTitle>
            <CardDescription>
              More tickets = more chances to win!
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-3">
              {TICKET_TIERS.map((tier) => (
                <button
                  key={tier.price}
                  onClick={() => setSelectedTier(tier)}
                  className={`p-4 rounded-lg border-2 transition-all text-left ${
                    selectedTier?.price === tier.price
                      ? 'border-primary bg-primary/10 shadow-md'
                      : 'border-border/50 hover:border-primary/50'
                  }`}
                >
                  <div className="text-xl font-bold text-foreground">{tier.label}</div>
                  <div className="text-sm text-muted-foreground">{tier.description}</div>
                  {selectedTier?.price === tier.price && (
                    <CheckCircle className="w-4 h-4 text-primary mt-2" />
                  )}
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Summary */}
        {selectedTier && (
          <Card className="mb-6 shadow-soft border-border/50 bg-gradient-sunset/5">
            <CardContent className="pt-6">
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Ticket Price</span>
                  <span className="font-medium">{selectedTier.label}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Entries</span>
                  <span className="font-medium">{selectedTier.entries}</span>
                </div>
                <div className="border-t border-border/50 pt-2 flex justify-between font-semibold">
                  <span>Total</span>
                  <span className="text-primary">{selectedTier.label}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={() => navigate(-1)}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            onClick={handlePurchase}
            disabled={!selectedTier || isProcessing}
            className="flex-1 bg-primary hover:bg-primary/90"
          >
            {isProcessing ? "Processing..." : "Purchase Tickets"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PrizeDraw;
